import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { Config } from "@config";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { GA4EventParameters } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import { updateUrl } from "@utils/url-update";
import autobind from "autobind-decorator";
import {
	PromisePool,
	generateUrlParams,
	getLocale,
	getUrlParameter,
	getUrlParameters,
	scrollToElement,
} from "bm-ui-kit";
import type { ReactNode } from "react";
import { Container } from "react-bootstrap";
import { InView } from "react-intersection-observer";
import type { ProductType } from "../AbstractLeadForm/types";
import type { CalculatorError } from "../CalculatorError";
import type { AbstractResponseItem, MarketingFilter, ResultListResponse } from "./types";

export interface AbstractListingCalculatorProps extends MeasureComponentProps {
	marketingFilter?: MarketingFilter;
	initialResult?: boolean;
	enableScrollToInitialResult?: boolean;
	highlightedBank?: string;
	fakeHeader?: boolean;
	alwaysHasFullPromoList?: boolean; // Mobil nézetben ne csak egy promós terméket jelenítsen meg
}
export interface AbstractListingCalculatorState<SearchParams, Request, ResponseItem extends AbstractResponseItem, Sort>
	extends MeasureComponentState {
	searchParams: SearchParams;
	request: Request;
	results: ResultListResponse<ResponseItem>;
	sort: Sort;
	error?: string;

	progress: boolean;

	wasVisible: boolean;

	isAutomaticResult: boolean; // Ha van előre kalkulált eredmény, vagy URL paraméterek alapján azonnal kalkulál
}

export abstract class AbstractListingCalculator<
	SearchParams,
	Request,
	ResponseItem extends AbstractResponseItem,
	Sort,
	Props extends AbstractListingCalculatorProps = AbstractListingCalculatorProps,
	State extends AbstractListingCalculatorState<
		SearchParams,
		Request,
		ResponseItem,
		Sort
	> = AbstractListingCalculatorState<SearchParams, Request, ResponseItem, Sort>,
> extends MeasureComponent<Props, State> {
	protected readonly promisePool: PromisePool = new PromisePool();
	private initValues: SearchParams;
	protected firstCalculation = true;
	protected calculationId: string = undefined;

	protected readonly marketingFilter: MarketingFilter = "normal";

	constructor(props: Props) {
		super(props);

		if (getUrlParameter("noCt")) {
			this.marketingFilter = "without-ct";
		}
	}

	protected getInitialState(): State {
		const { initialResult } = this.props;
		const searchParams = this.getSearchParamsFromUrl();
		const request = this.getRequestFromSearchParams(searchParams);
		this.initValues = searchParams;

		return {
			...super.getInitialState(),
			searchParams,
			request,
			results: undefined,
			wasVisible: Config.isSsr(),
			progress: false,
			sort: undefined,
			isAutomaticResult: !!initialResult || getUrlParameter("start") !== undefined,
		} as unknown as State;
	}

	public componentDidMount(): void {
		super.componentDidMount();

		const { initialResult } = this.props;
		const { sort } = this.state;
		const searchParams = getUrlParameters();

		if (initialResult || searchParams?.start !== undefined) {
			this.onPrivateSubmit(this.getSearchParamsFromUrl(), false);

			this.setState({
				isAutomaticResult: true,
				sort: searchParams?.sort || sort,
			});
		}
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	public render(): ReactNode {
		const { wasVisible } = this.state;

		return (
			<InView
				as="div"
				className={this.getClassName()}
				{...GoogleAnalytics4.getInViewProps()}
				skip={wasVisible}
				onChange={this.onVisibilityChange}
			>
				{this.renderContent()}
			</InView>
		);
	}

	protected renderHeader(title: ReactNode, subTitle: ReactNode): ReactNode {
		return (
			<Container className="text-white mb-3">
				<h1 className="display-1 display-bold mb-0">{title}</h1>
				<div className="display-4 d-md-block d-none">{subTitle}</div>
			</Container>
		);
	}

	protected abstract renderContent(): ReactNode;

	protected getSearchFormProps(): {
		initValues: SearchParams;
		onSubmit: (searchParams: SearchParams, isSort: boolean) => Promise<void>;
		onExport: (searchParams: SearchParams) => Promise<void>;
		highlightedBank?: string;
		ga4Event: GA4EventParameters;
	} {
		const { highlightedBank } = this.props;

		return {
			initValues: this.initValues,
			onSubmit: this.onPrivateSubmit,
			onExport: Config.getCurrentUser().hasRole(this.getProductType()) ? this.onPrivateExport : undefined,
			highlightedBank,
			ga4Event: {
				hasDetailed: this.hasDetailed(this.initValues),
				isDetailed: this.isDetailed(this.initValues),
				...this.getCommonEventParameters(),
			},
		};
	}

	protected getResultListProps(): {
		searchParams: SearchParams;
		request: Request;
		results: ResultListResponse<ResponseItem>;
		error: ReactNode;
		progress: boolean;
		sort: Sort;
		marketingFilter: MarketingFilter;
		highlightedBank?: string;
		productType: ProductType;
		alwaysHasFullPromoList?: boolean; // Mobil nézetben ne csak egy promós terméket jelenítsen meg
		onSortChange: (sort: Sort) => void;
		ga4Event: GA4EventParameters;
	} {
		const { marketingFilter = this.marketingFilter, highlightedBank, alwaysHasFullPromoList = false } = this.props;
		const { searchParams, request, results, progress, sort, error } = this.state;

		return {
			searchParams,
			request,
			results: error ? { normalList: [], highlightList: [] } : results,
			error,
			progress,
			sort,
			marketingFilter,
			highlightedBank,
			productType: this.getProductType(),
			alwaysHasFullPromoList,
			onSortChange: this.onSortChange,
			ga4Event: this.getCommonEventParameters(),
		};
	}

	@autobind
	protected onSortChange(sort: Sort): void {
		const { searchParams } = this.state;

		this.setState(
			{
				sort,
				progress: true,
			},
			() => {
				this.onPrivateSubmit(searchParams, true);
			},
		);

		this.sendEvent("BM_sort", {
			sort,
		});
	}

	@autobind
	private async onPrivateSubmit(searchParams: SearchParams, isSort: boolean): Promise<void> {
		const { enableScrollToInitialResult, initialResult } = this.props;
		const { isAutomaticResult } = this.state;
		this.setState({ progress: true });

		const request = this.getRequestFromSearchParams(searchParams);

		return this.promisePool
			.add(this.getResult(request, searchParams))
			.then((response) => {
				if (!isAutomaticResult) {
					updateUrl(this.getUrlBySearchParams(searchParams), "Összehasonlítás", searchParams);
				}

				this.promisePool.addTimeout(1_500).then(() => {
					const results = this.getResultList(request, searchParams, response);
					const hasResult = !!results?.normalList?.length;

					if (!isSort) {
						this.sendCalculationEvent(searchParams, hasResult ? [] : ["Nincs találat!"]);

						if (hasResult) {
							this.sendEvent("BM_result");
						}
					}

					this.setState({
						searchParams,
						request,
						results,
						error: undefined,
						progress: false,
						isAutomaticResult: false,
					});
				});
			})
			.catch((error: CalculatorError) => {
				this.sendCalculationEvent(searchParams, [error.message]);
				this.setState({ progress: false, error: error.message });

				logError(error);
			})
			.finally(() => {
				if (initialResult) {
					if (enableScrollToInitialResult) {
						scrollToElement("result", { offset: 0 });
					}
				} else {
					scrollToElement("result", { offset: 0 });
				}
			});
	}

	@autobind
	protected async onPrivateExport(searchParams: SearchParams): Promise<void> {
		return this.onExport(searchParams)
			.then((url: string) => {
				window.location.href = url;
			})
			.catch((error: Error) => logError(error));
	}

	protected abstract onExport(searchParams: SearchParams): Promise<string>;

	protected abstract getDefaultValues(): SearchParams;

	protected getSearchParamsFromUrl(url?: string): SearchParams {
		const defaultParams = { ...this.getDefaultValues() };
		const params = getUrlParameters(url);

		Object.keys(defaultParams).forEach((key) => {
			if (params[key] !== undefined) {
				defaultParams[key] = this.getParamsFromUrl(key, params[key]);
			}
		});

		return defaultParams as unknown as SearchParams;
	}

	protected getParamsFromUrl(_name: string, value: any): any {
		return value;
	}

	protected getUrlBySearchParams(searchParams: SearchParams): string {
		const { sort } = this.state;
		const { hash } = window.location;
		const urlParams: Record<string, any> = {
			sort,
			...searchParams,
			start: 0,
		};
		const defaultParams = { ...this.getDefaultValues() };

		if (getLocale() !== "hu_HU") {
			urlParams.lang = getLocale();
		}

		if (Config.getLeadSource() !== "BM") {
			urlParams.source = Config.getLeadSource();
		}

		const params = {};

		Object.keys(urlParams).forEach((key) => {
			if (urlParams[key] !== undefined && urlParams[key] !== defaultParams[key]) {
				params[key] = urlParams[key];
			}
		});

		return `${window.location.protocol}//${window.location.host}${window.location.pathname}?${generateUrlParams(
			params,
		)}${hash}`;
	}

	protected abstract getRequestFromSearchParams(searchParams: SearchParams): Request;

	protected abstract getResult(
		request: Request,
		searchParams: SearchParams,
	): Promise<ResultListResponse<ResponseItem>>;

	protected abstract getResultList(
		request: Request,
		searchParams: SearchParams,
		response: any,
	): ResultListResponse<ResponseItem>;

	protected abstract getProductType(): ProductType;

	protected getClassName(): string {
		return `calculator react-calculator`;
	}

	protected hasDetailed(searchParams: SearchParams): boolean {
		return this.getDetailedFieldsName(searchParams).length > 0;
	}

	protected isDetailed(searchParams: SearchParams, apiFields: string[] = []): boolean {
		const defaultValues = this.getDefaultValues();
		const detailedFieldsName = this.getDetailedFieldsName(searchParams);
		const detailedData = {} as unknown;
		Object.keys(searchParams).forEach((name) => {
			if (detailedFieldsName.includes(name)) {
				detailedData[name] = searchParams[name];
			}
		});

		return (
			Object.keys(defaultValues).find(
				(name) =>
					detailedFieldsName.includes(name) &&
					detailedData[name] !== undefined &&
					((!apiFields.includes(name) &&
						JSON.stringify(detailedData[name]) !== JSON.stringify(defaultValues[name])) ||
						(apiFields.includes(name) &&
							defaultValues[name].length > 0 &&
							JSON.stringify(detailedData[name]) !== JSON.stringify(defaultValues[name]))),
			) !== undefined
		);
	}

	protected getDetailedFieldsName(searchParams: SearchParams): string[] {
		const notDetailedFeilds = this.getBasicSearchFields();

		return Object.keys(searchParams).filter((fieldName) => !notDetailedFeilds.includes(fieldName));
	}

	protected abstract getBasicSearchFields(): string[];

	protected sendCalculationEvent(searchParams: SearchParams, koMessages: string[] = []): void {
		this.calculationId = GoogleAnalytics4.generateId("ci");

		this.sendEvent("BM_calculation", {
			hasDetailed: this.hasDetailed(searchParams),
			isDetailed: this.isDetailed(searchParams),
			koMessages: koMessages?.join("|"),
		});

		if (this.firstCalculation) {
			this.sendEvent("BM_defaultParams", this.getDefaultValues());
			this.firstCalculation = false;
		}

		this.sendEvent("BM_calculationParams", {
			...searchParams,
		});
	}

	protected getCommonEventParameters(): Partial<GA4EventParameters> {
		return {
			...super.getCommonEventParameters(),
			calculationId: this.calculationId,
		};
	}
}
